import dayjs from 'dayjs';
import type {
  Content,
  Margins,
  Table,
  TDocumentDefinitions,
  // eslint-disable-next-line node/no-missing-import
} from 'pdfmake/interfaces';
import type { Dossier, Finding } from '@owl-frontend/api-client/interface';
import { FindingDecisionDetailAcceptedReason } from '@owl-frontend/api-client/interface';
import { createLogger } from '@owl-lib/logger';
import { DISMISSED_SEPARATOR, PdfFields } from '../hooks/useExportDossier';

const EMPTY = '—';
const SECTION_MARGIN: Margins = [0, 0, 0, 16];
const logger = createLogger(__filename);
const separator = {
  table: {
    widths: ['*'],
    body: [
      [{ text: ' ', margin: 0, fontSize: 0 }],
      [{ text: ' ', margin: 0, fontSize: 0 }],
    ],
  },
  layout: {
    hLineWidth(i, node) {
      return i === 0 || i === node.table.body.length ? 0 : 1;
    },
    vLineWidth: () => 0,
    hLineColor: '#D9D9D9',
  },
};

const getBase64FromUrl = async (url) => {
  try {
    const data = await fetch(url);
    if (data.status !== 200) {
      logger.error(url, 'Error fetching image');
    } else {
      const blob = await data.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data);
        };
      });
    }
  } catch (e) {
    logger.error(e, 'Error creating base64 image');
    throw new Error('Error creating base64 image');
  }
};

const genReviewedByText = (dossier: Dossier): string => {
  if (dossier.conclusion === 'accepted') {
    return 'Accepted';
  }

  if (dossier.conclusion === 'dismissed') {
    return 'Dismissed';
  }

  if (dossier.status === 'closed') {
    return 'Closed';
  }

  return 'Reviewed';
};

const generateHeader = (
  dossier: Dossier,
  numFindings: number
): TDocumentDefinitions['content'][] => {
  const {
    clientDossierId,
    data: { names },
  } = dossier;

  const dossierName = `${names[0].firstName} ${names[0].lastName}`;

  return [
    {
      alignment: 'justify',
      width: 100,
      columns: [
        {
          stack: [
            {
              text: dossierName,
              style: 'header',
            },
            {
              text: `File ID: ${clientDossierId}`,
              style: 'subHeader',
            },
            {
              text: `${genReviewedByText(dossier)} by ${
                dossier.conclusionSetByName ?? dossier.updatedByName
              } on ${dayjs(dossier.conclusionSetAt ?? dossier.updatedAt).format(
                'MMM DD, YYYY'
              )}`,
              style: 'subHeader',
            },
          ],
          alignment: 'left',
        },
        {
          text: `${numFindings} Finding(s)`,
          style: 'headerExtra',
          alignment: 'right',
        },
      ],
    },
    {
      ...separator,
      layout: {
        ...separator.layout,
        hLineColor: '#fff',
      },
    },
  ];
};

const conclusionReason = (reason: string | undefined, messages): string => {
  if (
    messages[`dossierDetails.preReview.infeasibleReasons.options.${reason}`]
  ) {
    return messages[
      `dossierDetails.preReview.infeasibleReasons.options.${reason}`
    ];
  }

  if (messages[`dossierDetails.decision.dossier.details.${reason}`]) {
    return messages[`dossierDetails.decision.dossier.details.${reason}`];
  }

  return reason ?? '-';
};

const generateConclusion = (dossier: Dossier, messages) => {
  if (
    !dossier.conclusionDetails?.comment &&
    !dossier.conclusionDetails?.reason
  ) {
    return [];
  }

  return [
    {
      text: 'File comments',
      style: 'h2',
    },
    {
      text: conclusionReason(dossier.conclusionDetails.reason, messages),
      style: 'attributeValue',
    },
    // separator,
    {
      text: dossier.conclusionDetails.comment,
      style: 'attributeValue',
      margin: SECTION_MARGIN,
    },
  ] as Content[];
};

const generateSummaryCount = (
  acceptedFindings: Finding[],
  dismissedFindings: Finding[],
  informationalFindings: Finding[],
  messages
): TDocumentDefinitions['content'][] => {
  const summary = acceptedFindings.reduce(
    (acc, curr) => {
      if (curr.decisionDetails) {
        return {
          ...acc,
          [curr.decisionDetails.reason!]: acc[curr.decisionDetails.reason!] + 1,
        };
      }
      return {
        ...acc,
      };
    },
    {
      ...Object.values(FindingDecisionDetailAcceptedReason).reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: 0,
        }),
        {}
      ),
    }
  );

  const filteredSummary = Object.keys(summary).filter(
    (reason) => !!summary[reason]
  );

  return [
    {
      text: 'Summary',
      style: 'h2',
    },
    // separator,
    [
      ...filteredSummary.map((reason) => ({
        text: `• ${summary[reason]} ${
          messages[`dossiers.actions.downloadPdf.reasons.${reason}`] ?? reason
        }`,
        style: 'attributeValue',
      })),
      {
        text: `• ${dismissedFindings.length} dismissed finding(s)`,
        style: 'attributeValue',
      },
      {
        text: `• ${informationalFindings.length} informational finding(s)`,
        margin: SECTION_MARGIN,
        style: 'attributeValue',
      },
    ],
  ];
};

const TABLE_MARGINS = 8;

const generateTable = async (
  title: string,
  findings: Finding[],
  fields: string[],
  messages: {
    [k: string]: string;
  },
  screenshotPermission?: boolean
): Promise<TDocumentDefinitions['content'][]> => {
  const host = window.location.hostname;

  const screenshotsIndexMap: (
    | {
        [findingIndex: number]: string;
      }
    | undefined
  )[] = await Promise.all(
    findings.map(async (f, i) => {
      if (f.screenshot && 'imagePath' in f.screenshot) {
        const imageBase64 = await getBase64FromUrl(f.screenshot.imagePath);
        if (imageBase64 !== undefined && typeof imageBase64 === 'string') {
          return { [i]: imageBase64 };
        }
      }
    })
  );
  const filteredScreenshots = screenshotsIndexMap
    .filter((i) => i !== undefined)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  if (fields.length === 0) {
    return [];
  }
  return [
    {
      text: title,
      style: 'h2',
    },
    separator,
    findings.map((f, i) => {
      const body: Table['body'] = [];
      const index = { text: `${i + 1}.`, style: 'indexText' };
      if (fields.includes(PdfFields.Reason) && f.decisionDetails?.reason) {
        body.push([
          index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.reason'],
            style: 'attributeKey',
          },
          {
            text:
              messages[
                `dossierDetails.decision.findings.details.${f.decisionDetails.reason}`
              ] ?? EMPTY,
            style: 'attributeValue',
          },
        ]);
      }

      if (fields.includes(PdfFields.EventType)) {
        body.push([
          body.length > 0 ? { text: '' } : index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.eventType'],
            style: 'attributeKey',
          },
          {
            text: `${f.criterion.eventType} > ${f.criterion.categories.join(
              ' > '
            )}`,
            style: 'attributeValue',
          },
        ]);
      }

      if (fields.includes(PdfFields.EventDate)) {
        body.push([
          body.length > 0 ? { text: '' } : index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.eventDate'],
            style: 'attributeKey',
          },
          {
            text: dayjs(f.eventDate).format('MMM DD, YYYY'),
            style: 'attributeValue',
          },
        ]);
      }

      if (fields.includes(PdfFields.Source)) {
        body.push([
          body.length > 0 ? { text: '' } : index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.source'],
            style: 'attributeKey',
          },
          { text: f.originalUrl ?? EMPTY, style: 'attributeValue' },
        ]);
      }

      if (fields.includes(PdfFields.Description)) {
        body.push([
          body.length > 0 ? { text: '' } : index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.description'],
            style: 'attributeKey',
          },
          { text: f.content, style: 'attributeValue' },
        ]);
      }

      if (fields.includes(PdfFields.Comments)) {
        body.push([
          body.length > 0 ? { text: '' } : index,
          {
            text: messages['dossiers.actions.downloadPdf.fields.comments'],
            style: 'attributeKey',
          },
          {
            text: f.decisionDetails?.comment ?? EMPTY,
            style: 'attributeValue',
            margin: i === findings.length - 1 ? SECTION_MARGIN : [],
          },
        ]);
      }

      if (
        screenshotPermission &&
        fields.includes(PdfFields.Screenshots) &&
        f.screenshot &&
        'imagePath' in f.screenshot
      ) {
        if (filteredScreenshots && filteredScreenshots[i] != undefined) {
          body.push([
            body.length > 0 ? { text: '' } : index,
            {
              text: messages['dossiers.actions.downloadPdf.fields.screenshots'],
              style: 'attributeKey',
            },
            { image: filteredScreenshots[i], width: 400 },
          ]);
        } else {
          body.push([
            body.length > 0 ? { text: '' } : index,
            {
              text: messages['dossiers.actions.downloadPdf.fields.screenshots'],
              style: 'attributeKey',
            },
            {
              text: f.screenshot.imagePath
                ? `${host}${f.screenshot.imagePath}`
                : EMPTY,
              style: 'attributeValue',
              margin: i === findings.length - 1 ? SECTION_MARGIN : [],
            },
          ]);
        }
      }

      const result: TDocumentDefinitions['content'] = [
        {
          layout: 'noBorders',
          margin: [TABLE_MARGINS, TABLE_MARGINS, 0, TABLE_MARGINS],
          table: {
            widths: [16, 60, 448],
            body,
          },
        },
      ];

      return result;
    }),
  ];
};

export const generateDocumentDefinition = async (
  dossier: Dossier,
  findings: Finding[],
  fields: string[],
  fieldsDismissed: string[],
  fieldsInformational: string[],
  messages: {
    [k: string]: string;
  },
  screenshotPermission?: boolean
): Promise<TDocumentDefinitions> => {
  const {
    clientDossierId,
    data: { names },
  } = dossier;

  const dossierName = `${names[0].firstName} ${names[0].lastName}`;
  const acceptedFindings =
    fields.length > 0 ? findings.filter((f) => f.decision === 'accepted') : [];
  const dismissedFindings =
    fieldsDismissed.length > 0
      ? findings.filter((f) => f.decision === 'rejected')
      : [];
  const informationalFindings =
    fieldsDismissed.length > 0
      ? findings.filter((f) => f.decision === 'informational')
      : [];

  return {
    pageMargins: 20,
    content: [
      ...generateHeader(dossier, findings.length),
      ...generateConclusion(dossier, messages),
      ...generateSummaryCount(
        acceptedFindings,
        dismissedFindings,
        informationalFindings,
        messages
      ),
    ]
      .concat(
        acceptedFindings.length > 0
          ? [
              ...(await generateTable(
                `${acceptedFindings.length} Accepted finding(s)`,
                acceptedFindings,
                fields,
                messages,
                screenshotPermission
              )),
            ]
          : []
      )
      .concat(
        dismissedFindings.length > 0
          ? [
              ...(await generateTable(
                `${dismissedFindings.length} Dismissed finding(s)`,
                dismissedFindings,
                fieldsDismissed.map((f) => f.split(DISMISSED_SEPARATOR)[0]),
                messages,
                screenshotPermission
              )),
            ]
          : []
      )
      .concat(
        informationalFindings.length > 0
          ? [
              ...(await generateTable(
                `${informationalFindings.length} Informational finding(s)`,
                informationalFindings,
                fieldsInformational,
                messages,
                screenshotPermission
              )),
            ]
          : []
      )
      .concat(
        dossier.status === 'closed' && findings.length > 0
          ? [
              ...(await generateTable(
                `${findings.length} Finding(s)`,
                findings,
                fields,
                messages,
                screenshotPermission
              )),
            ]
          : []
      ),
    footer: (currentPage, pageCount) => ({
      margin: [20, 0],
      columns: [
        {
          width: '33%',
          text: `Page ${currentPage} of ${pageCount}`,
          style: 'footerText',
        },
        {
          width: '33%',
          text: `File ID ${clientDossierId}`,
          style: 'footerText',
          alignment: 'center',
          noWrap: true,
        },
        {
          width: '33%',
          text: dossierName,
          style: 'footerText',
          alignment: 'right',
        },
      ],
    }),

    /**
     * MARK: STYLING
     */

    styles: {
      // Header
      header: {
        fontSize: 16,
        bold: true,
      },
      subHeader: {
        fontSize: 10,
        margin: [0, 1, 0, 0],
      },
      h2: {
        fontSize: 12,
        bold: true,
      },
      headerExtra: {
        fontSize: 14,
      },
      // Dossier
      conclusionLabel: {
        fontSize: 10,
        bold: true,
      },
      // Findings
      indexText: {
        fontSize: 10,
      },
      attributeKey: {
        fontSize: 10,
        bold: true,
        color: '#433838',
      },
      attributeValue: {
        fontSize: 10,
        color: '#433838',
      },
      // Footer
      footerText: {
        fontSize: 9,
        color: '#433838',
      },
    },
  };
};
